/**
 * Die acht Zuschlaege der Preisliste vom 15.07.2026.
 *
 * Alle Zuschlaege sind Regeln, keine handgetippten Zeilen. Sie tragen eine
 * `sort_order`, weil ein Prozentzuschlag wissen muss, worauf er rechnet.
 *
 * WICHTIG: Lexware rechnet keine Prozente. Am 14.09.2026 getestet -- es gibt
 * drueben keinen Positionstyp "5 % auf die Summe darueber". Wir rechnen und
 * liefern absolute Betraege; die Grundlage steht in den `schritte` und von
 * dort im Positionstext (31-lexware-durchtest.md).
 */
import { geld } from "./typen.js";
/**
 * **Geht diese Position mit Bediener hinaus?** — die eine Stelle, an der das
 * entschieden wird.
 *
 * Was das Gerät verlangt, schlägt, was verkauft wurde: Ein Kran
 * (`nur_mit_person`) ist bedient, auch wenn an der Position kein Haken steht;
 * ein Gerät `ohne_person` ist es nie. Nur bei `wahlweise` entscheidet die
 * Position (`rental_order_item.with_operator`).
 */
export function mitBediener(pflicht, verkauftMit) {
    if (pflicht === 'nur_mit_person')
        return true;
    if (pflicht === 'ohne_person')
        return false;
    return verkauftMit;
}
/**
 * **Braucht diese Miete zwingend einen Menschen — und zwar einen benannten?**
 *
 * Luca, 17.09.2026: *„Ich habe den Kran genommen, aber ich wurde beim direkten
 * Vermieten gar nicht dazu aufgefordert, den Fahrer auszuwählen. … Ich kann ja
 * nicht den Auftrag verplanen, ohne zu sagen: Das machen wir so. Ich muss es
 * dann manuell nachholen."*
 *
 * `mitBediener` beantwortet, ob die Position **bedient** hinausgeht — das
 * entscheidet über Zuschläge. Diese hier beantwortet die andere Hälfte: ob der
 * Vorgang ohne eine gebuchte Person überhaupt vollständig ist. Ein Kran
 * (`nur_mit_person`) ist es nicht: Der Mensch ist eine Ressource (Regel 8), und
 * eine Ressource, die keiner disponiert hat, fehlt am Morgen der Übergabe.
 *
 * `wahlweise` mit Haken bleibt eine **Wahl**: Wer „mit Bediener" ankreuzt und
 * noch nicht weiß, wer fährt, soll den Anruf trotzdem abschließen können. Nur
 * was das Gerät verlangt, ist Pflicht.
 */
export function bedienerPflicht(pflicht) {
    return pflicht === 'nur_mit_person';
}
/**
 * **Steht dieses Geraet in der Liste der Regel?** — Gruppe ODER Typ.
 *
 * Die eine Frage hinter beiden Leserichtungen: `nur_gruppen` gilt, wenn sie
 * mit Ja beantwortet wird, `alle_ausser` (0143), wenn sie mit Nein
 * beantwortet wird. Deshalb steht sie hier einmal und nicht zweimal in
 * `regelGilt`.
 *
 * Die Untergruppen sind schon in `gruppen` enthalten: Wer den Dieselzuschlag
 * an „Baumaschine" haengt, meint auch „Minibagger" darunter -- aufgeloest
 * beim Laden (`dienste/preisfindung.ts`), damit die Kapsel keine Gruppenkette
 * kennen muss.
 */
function stehtInDerListe(regel, gruppeId, typId) {
    return (regel.gruppen ?? []).includes(gruppeId)
        || (typId !== null && (regel.typen ?? []).includes(typId));
}
/**
 * **Die Einheit einer Zuschlagsregel** — aus ihrer Art, nicht aus einer
 * Vermutung.
 *
 * Sie stand bis zum 18.09.2026 nur hier drin und galt damit nur fuer eine
 * gerechnete Zeile. Eine Zuschlagszeile, die als Position gespeichert wird
 * (Schnellvermietung, Merkliste), bekam keine — und `packages/lex` setzte
 * seine Vorgabe: Auf RE1002 stand bei der Versicherung je Miettag „3 Stueck".
 *
 * Darum ist sie jetzt exportiert und die eine Stelle, an der diese Frage
 * beantwortet wird: beim Anlegen der Position, an der Kante zum Beleg und im
 * Abrechnungslauf.
 */
export function einheitFuerZuschlagsart(art, metric = null) {
    switch (art) {
        case 'pro_tag':
            return 'einheit.tag';
        case 'pro_km':
            return 'einheit.km';
        case 'pro_einheit':
            return metric === null || metric === '' ? 'einheit.stueck' : `einheit.${metric.toLowerCase()}`;
        default:
            return 'einheit.pauschale';
    }
}
function einheitFuer(regel) {
    return einheitFuerZuschlagsart(regel.kind, regel.metric);
}
/**
 * Gilt die Regel fuer diese Position?
 *
 * `global` gilt ueberall, ausser der Typ hat sie abgeschaltet.
 * `nur_gruppen` gilt, wenn die Gruppe ODER der Typ in der Liste steht.
 * `alle_ausser` (Migration 0143) gilt, wenn **keines von beiden** darin steht
 * — dieselbe Liste, rueckwaerts gelesen. Eine Gerätegruppe, die es beim
 * Anlegen der Regel noch nicht gab, ist damit drin und nicht draussen.
 * Die Abschaltung am Typ schlaegt alle drei.
 *
 * **Und die Bedienung kommt obendrauf** (Migration 0096): `nur_mit_bediener`
 * verlangt eine bediente Position, `nur_ohne_bediener` eine unbediente — und
 * das zusaetzlich zur Gerätebindung, nicht statt ihrer. `bedient` ist schon
 * aufgeloest (`mitBediener`); wer es nicht kennt, fragt nach einer Position
 * ohne Bediener, und das ist der Regelfall.
 *
 * **Ein Schnappschuss von vor 0143 traegt `global` oder `nur_gruppen`** und
 * rechnet danach wie davor (Regel 4). Fehlt `scope_mode` ganz, gilt `global`
 * — der Fall, den der letzte `return` beantwortet.
 */
export function regelGilt(regel, gruppeId, typId = null, bedient = false) {
    if (!regel.active)
        return false;
    if (regel.abgeschaltet === true)
        return false;
    if (regel.operator_mode === 'nur_mit_bediener' && !bedient)
        return false;
    if (regel.operator_mode === 'nur_ohne_bediener' && bedient)
        return false;
    if (regel.scope_mode === 'nur_gruppen')
        return stehtInDerListe(regel, gruppeId, typId);
    if (regel.scope_mode === 'alle_ausser')
        return !stehtInDerListe(regel, gruppeId, typId);
    return true;
}
/** Die Fassung ohne Typ — fuer Aufrufer, die nur die Gruppe kennen. */
export function regelGiltFuerGruppe(regel, gruppeId) {
    return regelGilt(regel, gruppeId, null);
}
/**
 * Ist die Regel gewaehlt?
 *
 * Nicht abwaehlbare Regeln gelten immer. Abwaehlbare gelten, wenn sie in
 * `gewaehlt` stehen -- und wenn niemand gewaehlt hat, entscheidet
 * `preselected`. Das ist der Angebotsfall: die Haftungsbeschraenkung steht
 * vorgewaehlt drin und der Kunde kann sie streichen.
 */
function istGewaehlt(regel, gewaehlt) {
    if (!regel.is_optional)
        return true;
    if (gewaehlt === undefined)
        return regel.preselected;
    return gewaehlt.includes(regel.code);
}
/**
 * Die Zuschlaege einer Position, nach `sort_order` gerechnet.
 *
 * Zeilen mit `umfang: 'auftrag'` (CHECK, ANLIEF) kommen hier mit heraus,
 * gehoeren aber nur EINMAL auf den Beleg -- auch wenn drei Maschinen auf dem
 * Auftrag stehen. Dafuer gibt es `zuschlaegeZusammenfassen`.
 *
 * **Eine zuschlagsfreie Pauschale bekommt keine einzige Zeile** (0144). Das
 * wird hier entschieden und nicht in `regelGilt`: Es ist keine Aussage ueber
 * die Regel -- die gilt fuer dieses Geraet sehr wohl --, sondern ueber diese
 * eine Position. Dass dabei nichts mit `umfang: 'auftrag'` herauskommt, ist
 * genau richtig: Die Pauschale **loest** den Ruecknahme-Check nicht aus. Sie
 * **unterdrueckt** ihn aber auch nicht -- steht eine Baumaschine auf
 * demselben Vorgang, liefert DEREN Satz ihn, und
 * `zuschlaegeZusammenfassen` setzt ihn einmal auf den Beleg.
 */
export function zuschlaegeRechnen(eingabe) {
    if (eingabe.zuschlagsfrei === true)
        return [];
    const prozentBasis = eingabe.prozent_basis ?? 'nettomiete';
    const anwendbar = eingabe.regeln
        .filter((regel) => regelGilt(regel, eingabe.gruppe_id, eingabe.typ_id ?? null, eingabe.bedient ?? false))
        .filter((regel) => istGewaehlt(regel, eingabe.gewaehlt))
        .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
    const zeilen = [];
    let laufend = eingabe.nettomiete;
    for (const regel of anwendbar) {
        const schritte = [];
        let menge;
        let einzelpreis;
        let betrag;
        /* **Preis immer individuell** (0227, E1 — die Anlieferung). Kein Satz,
           kein Sockel, keine Zahl: Die Zeile sagt, dass sie dazukommt, und dass
           ihr Preis fehlt. Bis zum 04.10.2026 stand die Anlieferung hier mit Satz
           0,00 und Mindestbetrag 15,00 — und hiess je nach Ort 0,00, 15,00 oder
           „1 × 0,00 = 15,00" (R-12, V-07, V-02). */
        if (regel.preis_individuell === true) {
            zeilen.push({
                code: regel.code,
                netto: 0,
                menge: regel.kind === 'pro_tag' ? eingabe.miettage : 1,
                einzelpreis: 0,
                einheit_key: einheitFuer(regel),
                steuersatz: regel.tax_rate,
                reihenfolge: regel.sort_order,
                umfang: regel.scope,
                optional: regel.is_optional,
                vorgewaehlt: regel.preselected,
                lex_article_id: regel.lex_article_id,
                schritte: [{ key: 'zuschlag.preis_individuell', code: regel.code }],
                preis_fehlt: true,
            });
            continue;
        }
        switch (regel.kind) {
            case 'prozent': {
                const grundlage = prozentBasis === 'laufend' ? laufend : eingabe.nettomiete;
                menge = 1;
                betrag = (grundlage * regel.value) / 100;
                einzelpreis = geld(betrag);
                schritte.push({
                    key: 'zuschlag.prozent',
                    code: regel.code,
                    prozent: regel.value,
                    basis: regel.basis,
                    basis_betrag: geld(grundlage),
                    basis_art: prozentBasis,
                });
                break;
            }
            case 'pro_tag': {
                menge = eingabe.miettage;
                einzelpreis = regel.value;
                betrag = menge * regel.value;
                schritte.push({
                    key: 'zuschlag.pro_tag',
                    code: regel.code,
                    miettage: menge,
                    satz: regel.value,
                });
                break;
            }
            case 'pro_einheit':
            case 'pro_km': {
                const kennzahl = regel.metric ?? (regel.kind === 'pro_km' ? 'KM' : null);
                const gemessen = kennzahl === null ? 0 : (eingabe.mengen[kennzahl] ?? 0);
                // Ohne Menge keine Zeile: DIESEL und MAUT entstehen erst aus dem
                // Protokoll. Eine Zeile mit 0,00 waere eine rote Ampel ohne Anlass.
                if (gemessen <= 0)
                    continue;
                menge = gemessen;
                einzelpreis = regel.value;
                betrag = menge * regel.value;
                schritte.push({
                    key: 'zuschlag.pro_einheit',
                    code: regel.code,
                    metric: kennzahl,
                    menge,
                    satz: regel.value,
                });
                break;
            }
            case 'pauschale': {
                menge = 1;
                einzelpreis = regel.value;
                betrag = regel.value;
                schritte.push({ key: 'zuschlag.pauschale', code: regel.code, satz: regel.value });
                break;
            }
        }
        // Sockel: "Mindestpauschale, darueber der Satz" -- fuer Regeln, die beide
        // Spalten gepflegt haben. Im Seed vom 15.07.2026 ist das keine.
        if (regel.min_charge_net !== null) {
            const inklusive = regel.included_units ?? 0;
            const mehr = Math.max(0, menge - inklusive);
            betrag = regel.min_charge_net + mehr * regel.value;
            schritte.push({
                key: 'zuschlag.sockel',
                code: regel.code,
                mindestbetrag: regel.min_charge_net,
                inklusive,
                darueber: mehr,
            });
        }
        const netto = geld(betrag);
        if (netto === 0 && regel.value === 0) {
            // ANLIEF steht mit 0,0000 in der Liste: die Pauschale ist noch nicht
            // beziffert. Wir erfinden keine Zahl, wir melden sie als fehlend.
            schritte.push({ key: 'zuschlag.betrag_fehlt', code: regel.code });
        }
        schritte.push({ key: 'zuschlag.netto', code: regel.code, betrag: netto });
        // R-12 (Durchgang 03.10.2026): Bestimmt der Sockel den Betrag, muss die
        // Zeile ihn auch zeigen. Vorher stand die Anlieferung (Satz 0,00,
        // Mindestbetrag 15,00) im Vorschlag als „1 × 0,00 = 15,00", drueben in
        // Lexware als „1 × 15,00". Dieselbe Form wie auf Angebot und AB
        // (`mitte/zuschlagszeilen.ts`): Geht Menge x Satz nicht auf den Betrag
        // auf, ist die Zeile **eine Pauschale mit dem Betrag**. Der Satz im
        // Stamm bleibt, was er ist; der Rechenweg nennt ihn weiter.
        let einheitKey = einheitFuer(regel);
        if (regel.min_charge_net !== null && geld(menge * einzelpreis) !== netto) {
            menge = 1;
            einzelpreis = netto;
            einheitKey = 'einheit.pauschale';
        }
        zeilen.push({
            code: regel.code,
            netto,
            menge,
            einzelpreis,
            einheit_key: einheitKey,
            steuersatz: regel.tax_rate,
            reihenfolge: regel.sort_order,
            umfang: regel.scope,
            optional: regel.is_optional,
            vorgewaehlt: regel.preselected,
            lex_article_id: regel.lex_article_id,
            schritte,
        });
        if (regel.scope === 'position')
            laufend = geld(laufend + netto);
    }
    return zeilen;
}
/**
 * Alle Positionszeilen eines Vorgangs zusammenfuehren.
 *
 * Positionsbezogene Zuschlaege bleiben, wie sie sind. Auftragsbezogene
 * (CHECK, ANLIEF) werden auf eine Zeile je Code eingedampft: Der
 * Ruecknahme-Check kostet 15 EUR EINMAL, auch wenn drei Maschinen auf dem
 * Auftrag stehen -- und niemand merkt es, bis ein Kunde nachrechnet.
 */
export function zuschlaegeZusammenfassen(zeilen) {
    const ergebnis = [];
    const gesehen = new Set();
    for (const satz of zeilen) {
        for (const zeile of satz) {
            if (zeile.umfang === 'auftrag') {
                if (gesehen.has(zeile.code))
                    continue;
                gesehen.add(zeile.code);
            }
            ergebnis.push(zeile);
        }
    }
    return ergebnis.sort((a, b) => a.reihenfolge - b.reihenfolge || a.code.localeCompare(b.code));
}
/**
 * Dieselbe Rechnung wie `zuschlaegeRechnen` -- und daneben die Regeln, die
 * heute noch keinen Betrag haben koennen.
 *
 * **Warum das eine zweite Funktion ist und keine Fahne an der ersten.**
 * `zuschlaegeRechnen` fuettert den Abrechnungslauf. Der darf eine Regel ohne
 * gemessene Menge nicht sehen: Eine Zeile mit 0,00 auf einer Rechnung ist eine
 * Behauptung, und Regel 5 verlangt, dass zweimal Rechnen dasselbe ergibt. Wer
 * dort einen Schalter einbaut, riskiert genau die Zeile, die niemand bestellt
 * hat.
 *
 * Der Beleg dagegen zeigt eine **Erwartung** -- was nach heutigem Stand
 * dazukommt --, und dort ist das Weglassen der Fehler: Luca, 16.09.2026, zur
 * Vorschau der Schnellerfassung: *„Sonst sieht eine Null aus wie ‚kostet
 * nichts'."* Also wird sie genannt, mit Satz und Einheit und **ohne Betrag**.
 *
 * Die Prozentzuschlaege stehen in `zeilen`, nicht hier: Sie HABEN einen Betrag,
 * er haengt nur an einer Nettomiete, die sich noch aendern kann. Ihr Satz steckt
 * im Schritt `zuschlag.prozent` -- wer die Zeile schreibt, liest ihn dort.
 */
export function regelInKennzahlGilt(eingabe, kennzahl) {
    /* **Greift an dieser Position eine Regel je Einheit in dieser Kennzahl?**
       (Entscheidung 06.10.2026, Maut je km.) Dieselbe Auswahl wie
       `zuschlaegeRechnen` — zuschlagsfrei, gilt, gewählt —, nur ohne Menge:
       Die Regel ist da, auch wenn noch kein Kilometer gemessen ist. Daran
       hängt die rote Ampel „Kilometer fehlen" (`abrechnung.km_fehlt`). */
    if (eingabe.zuschlagsfrei === true)
        return false;
    return eingabe.regeln
        .filter((regel) => regelGilt(regel, eingabe.gruppe_id, eingabe.typ_id ?? null, eingabe.bedient ?? false))
        .filter((regel) => istGewaehlt(regel, eingabe.gewaehlt))
        .some((regel) => (regel.kind === 'pro_einheit' || regel.kind === 'pro_km')
        && regel.preis_individuell !== true
        && (regel.metric ?? (regel.kind === 'pro_km' ? 'KM' : null)) === kennzahl);
}
export function zuschlaegeErwartet(eingabe) {
    /* **Zuschlagsfrei heisst auch: nichts zu erwarten** (0144). Stuende der
       Diesel hier unter `offen`, saehe der Beleg so aus, als komme nach der
       Ruecknahme noch etwas -- und die Pauschale waere wieder nicht die Zahl,
       die sie sein soll. */
    if (eingabe.zuschlagsfrei === true)
        return { zeilen: [], offen: [] };
    const zeilen = zuschlaegeRechnen(eingabe);
    const gerechnet = new Set(zeilen.map((z) => z.code));
    const offen = eingabe.regeln
        .filter((regel) => regelGilt(regel, eingabe.gruppe_id, eingabe.typ_id ?? null, eingabe.bedient ?? false))
        .filter((regel) => istGewaehlt(regel, eingabe.gewaehlt))
        .filter((regel) => regel.kind === 'pro_einheit' || regel.kind === 'pro_km')
        .filter((regel) => !gerechnet.has(regel.code))
        .map((regel) => ({
        code: regel.code,
        art: regel.kind,
        satz: regel.value,
        einheit_key: einheitFuer(regel),
        metric: regel.metric ?? (regel.kind === 'pro_km' ? 'KM' : null),
        steuersatz: regel.tax_rate,
        reihenfolge: regel.sort_order,
        umfang: regel.scope,
        optional: regel.is_optional,
        vorgewaehlt: regel.preselected,
        lex_article_id: regel.lex_article_id,
    }))
        .sort((a, b) => a.reihenfolge - b.reihenfolge || a.code.localeCompare(b.code));
    return { zeilen, offen };
}
//# sourceMappingURL=zuschlag.js.map